import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import type { AxiosError } from "axios";
import {
  AdminCategoriesApi,
  type AdminCategoriesApiAdminCategoriesCategoryIdPutRequest,
  type AdminCategoriesApiAdminCategoriesPostRequest,
  type AdminCategoryReadModel,
  type AdminCategoryResponse,
  CategoryHighlightedEventApi,
  type PublicCategoryType,
} from "mws-catalog-ts-sdk";
import { CategoriesApi as PublicCategoriesApiV2 } from "mws-catalog-ts-sdk-v2-public";
import { type Ref, unref } from "vue";

import type {
  Guid,
  PaginatedResult,
  UseQueryOptionsOmitQueryKey,
} from "@/common/types";

const catalogApiUrl = import.meta.env.VITE_CATALOG_API;
const categoryEventHighlightApi = new CategoryHighlightedEventApi(
  undefined,
  catalogApiUrl,
);

const adminCategoriesApi = new AdminCategoriesApi(undefined, catalogApiUrl);
const publicCategoriesApiV2 = new PublicCategoriesApiV2(
  undefined,
  catalogApiUrl,
);

export const getPublicCategory = async (slug: string, platformKey: string) => {
  const response = await publicCategoriesApiV2.v2CategoriesSlugGet({
    slug,
    xPlatformKey: platformKey,
  });

  return response.data;
};

export const useCategoriesCollectionQuery = (
  searchTerm: Ref<string>,
  pageSize: Ref<number>,
  offset: Ref<number>,
  filters: Ref<{ type?: PublicCategoryType; categoryIds?: Guid[] }>,
  queryOptions?: UseQueryOptionsOmitQueryKey<
    PaginatedResult<AdminCategoryResponse>
  >,
) => {
  const queryKey = ["categories", { searchTerm, pageSize, offset, filters }];

  const queryFn = async () => {
    const response = await adminCategoriesApi.adminCategoriesGet({
      searchTerm: unref(searchTerm),
      pageSize: unref(pageSize),
      offset: unref(offset),
      type: unref(filters).type,
      categoryIds: unref(filters).categoryIds?.join(","),
    });

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
    ...queryOptions,
  });
};

export const useCategoryByIdQuery = (
  id: Ref<string>,
  options?: UseQueryOptionsOmitQueryKey<AdminCategoryReadModel>,
) => {
  const queryKey = ["categories", id];
  const queryFn = async () => {
    const response = await adminCategoriesApi.adminCategoriesCategoryIdGet({
      categoryId: unref(id),
    });

    return response.data;
  };

  return useQuery({
    queryKey,
    queryFn,
    ...options,
  });
};

export const useUpdateCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestData,
    }: {
      requestData: AdminCategoriesApiAdminCategoriesCategoryIdPutRequest;
    }) => {
      const response =
        await adminCategoriesApi.adminCategoriesCategoryIdPut(requestData);

      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["categories", data.id],
      });
    },
  });
};

export const useCreateCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      requestData: AdminCategoriesApiAdminCategoriesPostRequest,
    ) => {
      const response =
        await adminCategoriesApi.adminCategoriesPost(requestData);

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
    },
  });
};

export const useDeleteCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      categoryId: Guid;
      newCategoryId: Guid | null;
    }) => {
      const response = await adminCategoriesApi.adminCategoriesCategoryIdDelete(
        {
          categoryId: request.categoryId,
          newCategoryId: request.newCategoryId || undefined,
        },
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["categories"],
      });
    },
  });
};

export const useAddEventsToCategoryMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { categoryId: string; eventIds: string[] }) => {
      const response =
        await adminCategoriesApi.adminCategoriesCategoryIdEventsPost({
          categoryId: request.categoryId,
          addEventsToCategoryRequest: {
            eventIds: request.eventIds,
          },
        });

      return response.data;
    },
    onSuccess: (_, variables) => {
      // events by category
      queryClient.invalidateQueries({
        queryKey: ["events", variables.categoryId],
      });

      // event details
      variables.eventIds.forEach((eventId) => {
        queryClient.invalidateQueries({
          queryKey: ["events", eventId],
        });
      });
    },
  });
};

export const useDeleteEventFromCategoryMutation = (
  onSuccess?: () => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: { categoryId: string; eventId: string }) => {
      const response =
        await adminCategoriesApi.adminCategoriesCategoryIdEventsEventIdDelete({
          categoryId: request.categoryId,
          eventId: request.eventId,
        });

      return response.data;
    },
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });

      if (onSuccess) {
        onSuccess();
      }
    },
  });
};

export const useAddEventToCategoryHighlightsMutation = () => {
  const queryClient = useQueryClient();

  const mutationFn = async (request: {
    categoryId: string;
    eventId: string;
  }) => {
    const response =
      await categoryEventHighlightApi.adminCategoriesCategoryIdHighlightedEventsPost(
        {
          categoryId: request.categoryId,
          body: request.eventId,
        },
      );

    return response.data;
  };

  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["events", variables.categoryId],
      });
    },
  });
};

export const useDeleteEventFromCategoryHighlightsMutation = (
  onSuccess?: () => void,
  onError?: (error: AxiosError) => void,
) => {
  const queryClient = useQueryClient();

  const mutationFn = async (request: {
    categoryId: string;
    eventId: string;
  }) => {
    const response =
      await categoryEventHighlightApi.adminCategoriesCategoryIdHighlightedEventsDelete(
        { categoryId: request.categoryId, body: request.eventId },
      );

    return response.data;
  };

  return useMutation({
    mutationFn,
    onError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["events"],
      });

      if (onSuccess) {
        onSuccess();
      }
    },
  });
};
